import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Select } from "@/shared/components/ui/select";
import {
  OrganizationService,
  CreateOrganizationPayload,
} from "@/shared/services/organization.service";
import { TariffService, Tariff } from "@/shared/services/tariff.service";
import { PlusCircle } from "lucide-react";

// Form schema with validation
const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Organization name must be at least 2 characters." }),
  description: z.string().optional(),
  tariff_id: z.string({ required_error: "Please select a tariff." }),
  owner_name: z
    .string()
    .min(2, { message: "Owner name must be at least 2 characters." }),
  owner_email: z
    .string()
    .email({ message: "Please enter a valid email address." }),
  owner_password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters." })
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, {
      message:
        "Password must contain at least one uppercase letter, one lowercase letter, and one number.",
    }),
  is_internal: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface AddOrganizationModalProps {
  onOrganizationAdded: () => void;
}

const AddOrganizationModal: React.FC<AddOrganizationModalProps> = ({
  onOrganizationAdded,
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      tariff_id: "",
      owner_name: "",
      owner_email: "",
      owner_password: "",
      is_internal: false,
    },
  });

  // Load tariffs when modal opens
  useEffect(() => {
    if (open) {
      const fetchTariffs = async () => {
        try {
          const tariffsData = await TariffService.getAll();
          setTariffs(tariffsData);
          // Set default tariff if available
          if (tariffsData.length > 0) {
            form.setValue("tariff_id", tariffsData[0].id);
          }
        } catch (err) {
          setError("Failed to load tariffs");
          console.error("Error loading tariffs:", err);
        }
      };
      fetchTariffs();
    }
  }, [open, form]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.reset();
      setError(null);
    }
  }, [open, form]);

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await OrganizationService.create(data as CreateOrganizationPayload);
      setOpen(false);
      onOrganizationAdded();
    } catch (err) {
      setError("Failed to create organization");
      console.error("Error creating organization:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-1">
          <PlusCircle className="h-4 w-4" />
          Add Organization
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Organization</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter description (optional)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_internal"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Internal Organization</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Check this if this is an internal organization
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tariff_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Tariff</FormLabel>
                  <FormControl>
                    <Select {...field}>
                      <option value="" disabled>
                        Select a tariff
                      </option>
                      {tariffs.map((tariff) => (
                        <option key={tariff._id} value={tariff._id}>
                          {tariff.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="border-t pt-4 mt-6">
              <h3 className="text-md font-medium mb-4">Owner Information</h3>

              <FormField
                control={form.control}
                name="owner_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Owner Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter owner's name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="owner_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Owner Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter owner's email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="owner_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Owner Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter owner's password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Organization"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOrganizationModal;
